import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, Paper, FormGroup, Stack, Switch, Select, MenuItem, Tabs, Tab, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Grid from "@mui/material/Grid2";
import { styled } from '@mui/material/styles';
import theme from '../../../theme';
import CheckIcon from '@mui/icons-material/Check';
import { LIVE_PRICES, AUTOMATED_PRICES, LIVE_AUTO_PRICES } from './pricingData';
import { config } from '../../../config';

// AntSwitch component from SubscriptionDetails
const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 24,
    padding: 0,
    display: 'flex',
    borderRadius: 50,
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 15,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(24px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 2,
        top: 1,
        '&.Mui-checked': {
            transform: 'translateX(19px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#22B060',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 18,
        height: 18,
        borderRadius: 24,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: '#22B060',
        boxSizing: 'border-box',
    },
}));

interface PricingSectionProps {
    currentPlanId?: string;
    currentAttendees?: number;
    onPlanSelect: (planId: string) => void;
    hideTabs?: boolean;
    isSaleLive?: boolean;
    id?: string;
    subscriptionStatus?: string;
}

// Main component
const PricingSectionV2: React.FC<PricingSectionProps> = ({ currentPlanId, currentAttendees, onPlanSelect, hideTabs = false, isSaleLive, id, subscriptionStatus }) => {
    // Use config.isSaleLive as default if prop is not provided
    const saleLive = isSaleLive ?? config.isSaleLive;

    // State
    const [planDuration, setPlanDuration] = useState<'monthly' | 'yearly'>('yearly');
    const [tabValue, setTabValue] = useState(0);

    const [liveValue, setLiveValue] = useState("50");
    const [automatedValue, setAutomatedValue] = useState("3000");
    const [liveAutomatedValue, setLiveAutomatedValue] = useState("200");
    const [showAllFeatures, setShowAllFeatures] = useState(false);

    // Auto-detect current plan configuration
    useEffect(() => {
        if (currentPlanId && currentAttendees) {
            // Set plan duration based on current plan
            if (currentPlanId.includes('Yearly')) {
                setPlanDuration('yearly');
            } else if (currentPlanId.includes('Monthly')) {
                setPlanDuration('monthly');
            }

            // Set attendee values based on current plan
            if (currentPlanId.includes('Live-Webinars')) {
                // Find matching live plan attendee value
                const matchingLivePlan = LIVE_PRICES.find(plan => 
                    plan.value === currentAttendees.toString()
                );
                if (matchingLivePlan) {
                    setLiveValue(matchingLivePlan.value);
                }
            } else if (currentPlanId.includes('Automated-Gold')) {
                // Find matching automated plan attendee value
                const matchingAutomatedPlan = AUTOMATED_PRICES.find(plan => 
                    plan.value === currentAttendees.toString()
                );
                if (matchingAutomatedPlan) {
                    setAutomatedValue(matchingAutomatedPlan.value);
                }
            } else if (currentPlanId.includes('All-In-One')) {
                // Find matching live+automated plan attendee value
                const matchingLiveAutoPlan = LIVE_AUTO_PRICES.find(plan => 
                    plan.value === currentAttendees.toString()
                );
                if (matchingLiveAutoPlan) {
                    setLiveAutomatedValue(matchingLiveAutoPlan.value);
                }
            }
        }
    }, [currentPlanId, currentAttendees]);

    // Handlers
    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPlanDuration(e.target.checked ? 'yearly' : 'monthly');
    };

    const getStartForFree = (planType: 'free' | 'live' | 'automated' | 'liveAuto') => {
        let plan, formattedPlanType, attendees;

        if (planType === 'live') {
            plan = LIVE_PRICES.find(opt => opt.value === liveValue) || LIVE_PRICES[0];
            formattedPlanType = 'Live-Webinars';
            attendees = plan.value; // e.g., "50 Attendees" -> "50"
        } else if (planType === 'automated') {
            plan = AUTOMATED_PRICES.find(opt => opt.value === automatedValue) || AUTOMATED_PRICES[0];
            formattedPlanType = 'Automated-Gold';
            attendees = plan.value; // e.g., "200 Attendees" -> "200"
        } else if (planType === 'liveAuto') {
            plan = LIVE_AUTO_PRICES.find(opt => opt.value === liveAutomatedValue) || LIVE_AUTO_PRICES[0];
            formattedPlanType = 'All-In-One';
            attendees = plan.value; // e.g., "50 Live & 200 Automated" -> "50-200"
        } else if (planType === 'free') {
            formattedPlanType = 'free-trial-plan';
            onPlanSelect(formattedPlanType);
            return;
        }

        const currency = 'USD';
        const duration = planDuration.charAt(0).toUpperCase() + planDuration.slice(1); // "Monthly" or "Yearly"

        const planId = `${formattedPlanType}-${currency}-${duration}-${attendees}`;
        onPlanSelect(planId);
    };

    const contactSales = () => {
        window.location.href = `${process.env.REACT_APP_EASYWEBINAR_URL}/demo/`;
    };

    // In Live Webinars card
    const liveSelected = LIVE_PRICES.find(opt => opt.value === liveValue) || LIVE_PRICES[0];
    // In Automated Webinars card
    const automatedSelected = AUTOMATED_PRICES.find(opt => opt.value === automatedValue) || AUTOMATED_PRICES[0];
    // In Live + Automated card
    const liveAutoSelected = LIVE_AUTO_PRICES.find(opt => opt.value === liveAutomatedValue) || LIVE_AUTO_PRICES[0];

    // Memoized FEATURES array
    const FEATURES = useMemo(() => [
        // Session Duration & Capacity
        {section: 'Session Duration & Capacity'},
        { label: '2 Hour Session Duration (Live)', plans: [true, false, false, false], info: 'Maximum 2 hours for live webinar sessions.' },
        { label: '4 Hour Session Duration (Live)', plans: [false, true, false, true], info: 'Maximum 4 hours for live webinar sessions.' },
        { label: 'Unlimited Session Duration (Automated)', plans: [false, true, true, true], info: 'No time limit for automated webinar sessions.' },
        { label: '1 Host', plans: [true, true, true, true], info: 'One main presenter for the webinar.' },
        { label: '1 Co-Host/Moderator', plans: [true, false, false, false], info: 'One additional co-host or moderator.' },
        { label: '3 Co-Hosts/Moderators', plans: [false, true, true, true], info: 'Up to three additional co-hosts or moderators.' },

        // Live & Automated
        { section: 'Live & Automated' },
        { label: 'EasyCast for YouTube Live streaming', plans: [false, false, false, true], info: 'Stream directly to YouTube Live.' },
        { label: 'Live Chat with Automated sessions', plans: [false, true, true, true], info: 'Enable live chat for automated webinars.' },
        { label: 'Reporting & Analytics', plans: [true, true, true, true], info: 'Comprehensive analytics and reporting for your webinars.' },
        { label: 'Custom landing pages + embed forms', plans: [true, true, true, true], info: 'Create custom landing pages and embed registration forms.' },
        { label: 'Unlimited Live Webinars', plans: [false, 'Up to 4 Live Webinars', true, true], info: 'Host unlimited live webinars (limits may apply to some plans).' },
        { label: 'Green Room Access', plans: [true, false, true, true], info: 'Private pre-webinar chat for presenters.' },
        { label: 'Automated Webinars', plans: [false, true, true, true], info: 'Run fully automated webinars.' },
        { label: 'Evergreen Webinars', plans: [false, true, true, true], info: 'Fully automated, on-demand webinars.' },
        { label: 'Timed polls, CTAs (Automated)', plans: [false, true, true, true], info: 'Schedule polls and CTAs in automated webinars.' },
        { label: 'Dynamic Presentation Control', plans: [true, true, true, true], info: 'Control presentations dynamically during webinars.' },
        { label: 'Real-time offers, polls, Q&A', plans: [true, true, true, true], info: 'Engage attendees with real-time offers, polls, and Q&A.' },
        { label: 'Instant replay recordings', plans: [true, true, true, true], info: 'Access instant replays of your webinars.' },
        { label: 'Simulated Chat (Automated)', plans: [false, true, true, true], info: 'Pre-scripted chat shown in simulive webinars.' },
        { label: 'Attendee Spotlight', plans: [true, true, true, true], info: 'Highlight attendees during the webinar.' },
        { label: 'One-Click Registration', plans: [true, true, true, true], info: 'Let users register with a single click.' },
        { label: 'Built-in social sharing', plans: [true, true, true, true], info: 'Let users share your webinar on social media.' },
        { label: 'Calendar integration', plans: [true, true, true, true], info: 'Integrate webinars with calendar apps.' },
        { label: 'Smart engagement + conversion tracking', plans: [true, true, true, true], info: 'Track attendee engagement and conversions.' },
        { label: 'Email campaigns with intelligent targeting', plans: [true, true, true, true], info: 'Send targeted email campaigns to your audience.' },
        { label: 'API Access', plans: [false, false, false, true], info: 'Access platform features via API.' },

        // Storage and Add-on Accounts
        { section: 'Storage and Add-on Accounts' },
        { label: '2 GB Storage Space', plans: [false, true, true, true] },
        { label: '4 GB Storage Space', plans: [false, false, true, true] },
        { label: 'Unlimited Storage Space', plans: [false, false, false, true] },
        { label: 'Unlimited Team Members', plans: [false, true, true, true] },

        // RM & Marketing tools Integrations
        { section: 'CRM & Marketing tools Integrations' },
        { label: 'Zapier Integration', plans: [true, true, true, true] },
        { label: 'Mailchimp', plans: [true, true, true, true] },
        { label: 'Aweber', plans: [true, true, true, true] },
        { label: 'I-contact', plans: [true, true, true, true] },
        { label: 'Keap', plans: [true, true, true, true] },
        { label: 'GetResponse', plans: [true, true, true, true] },
        { label: 'Constant Contact', plans: [true, true, true, true] },
        { label: 'Go-High Level', plans: [true, true, true, true] },
        { label: 'Hubspot', plans: [true, true, true, true] },
        { label: 'Salesforce Pardot', plans: [false, false, false, true] },
        { label: 'Active Campaign', plans: [true, true, true, true] },
        { label: 'Kit', plans: [true, true, true, true] },
        { label: 'Drip', plans: [true, true, true, true] },
        { label: 'Sendgrid', plans: [true, true, true, true] },
        { label: 'Ontraport', plans: [true, true, true, true] },

        // Support
        { section: 'Support' },
        { label: 'Standard Support', plans: [false, true, true, true] },
        { label: 'Monthly 1:1 Strategy Call with CSM', plans: [false, false, true, true] },
        { label: 'Dedicated Account Manager', plans: [false, false, false, true] },
        { label: 'VIP Support & SLA', plans: [false, false, false, true] },
        { label: 'Live Session Monitoring', plans: [false, false, false, true] },

        // Security
        { section: 'Security' },
        { label: 'GDPR Compliance', plans: [true, true, true, true] },
        { label: 'SSO Login', plans: [true, true, true, true] },
        { label: 'Presenter control panel', plans: [true, true, true, true] },
        { label: 'Two-Factor Authentication (2FA)', plans: [true, true, true, true] },

        // Custom Services
        { section: 'Custom Services' },
        { label: 'White Labeling', plans: [false, 'add-on', 'add-on', true] },
        { label: 'Custom Domain', plans: [false, 'add-on', 'add-on', true] },
        { label: 'Funnel Audit & Strategy Sessions', plans: [false, true, true, true] },
        { label: 'Priority Access to Beta Releases', plans: [false, false, false, true] },
    ], [liveSelected, automatedSelected, liveAutoSelected]);

    // Helper function to check if this is the current plan
    const isCurrentPlan = (planType: 'free' | 'live' | 'automated' | 'liveAuto') => {
        if (!currentPlanId) return false;

        if (planType === 'free') {
            // For free plan, only check the plan ID
            return currentPlanId.includes('free-trial-plan');
        }

        // Check if the current plan ID contains the plan type
        let planTypeMatch = false;
        let attendeeMatch = false;
        let billingFrequencyMatch = false;

        // Check billing frequency match
        const currentBillingFrequency = planDuration === 'yearly' ? 'Yearly' : 'Monthly';
        billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

        if (planType === 'live') {
            planTypeMatch = currentPlanId.includes('Live-Webinars');
            attendeeMatch = currentAttendees === parseInt(liveValue);
        } else if (planType === 'automated') {
            planTypeMatch = currentPlanId.includes('Automated-Gold');
            attendeeMatch = currentAttendees === parseInt(automatedValue);
        } else if (planType === 'liveAuto') {
            planTypeMatch = currentPlanId.includes('All-In-One');
            attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
        }

        return planTypeMatch && attendeeMatch && billingFrequencyMatch;
    };

    // Helper function to check if this is the current trial plan
    const isCurrentTrialPlan = (planType: 'free' | 'live' | 'automated' | 'liveAuto') => {
        // Check if subscription is in trial status
        if (subscriptionStatus !== 'in_trial' || !currentPlanId) return false;

        if (planType === 'free') {
            return currentPlanId.includes('free-trial-plan');
        }

        // For trial plans, check if the current plan matches the plan type, attendee count, and billing frequency
        let planTypeMatch = false;
        let attendeeMatch = false;
        let billingFrequencyMatch = false;

        // Check billing frequency match
        const currentBillingFrequency = planDuration === 'yearly' ? 'Yearly' : 'Monthly';
        billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

        if (planType === 'live') {
            planTypeMatch = currentPlanId.includes('Live-Webinars');
            attendeeMatch = currentAttendees === parseInt(liveValue);
        } else if (planType === 'automated') {
            planTypeMatch = currentPlanId.includes('Automated-Gold');
            attendeeMatch = currentAttendees === parseInt(automatedValue);
        } else if (planType === 'liveAuto') {
            planTypeMatch = currentPlanId.includes('All-In-One');
            attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
        }

        return planTypeMatch && attendeeMatch && billingFrequencyMatch;
    };

    // Render main component
    return (
        <>
            <Box className="v3-pricing-plan-table" sx={{ my: 4 }} id={id}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px" sx={{ mb: 3 }}>
                    {!hideTabs && <Tabs
                        value={tabValue}
                        onChange={(_, newValue) => setTabValue(newValue)}

                        centered
                    >
                        <Tab label="Plans" sx={{ textTransform: 'capitalize', fontSize: { xs: '14px', sm: '16px' }, padding: { xs: '6px 12px' } }} />
                        <Tab label="Compare Plans" sx={{ textTransform: 'capitalize', fontSize: { xs: '14px', sm: '16px' }, padding: { xs: '6px 12px' } }} />
                    </Tabs>
                    }
                    {/* {tabValue === 0 ? ( */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap="16px"
                        ml="auto"
                        mb={2}
                    >
                        {saleLive ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: '#F5F5F5',
                                    borderRadius: '24px',
                                    padding: '4px',
                                    border: '1px solid #E0E0E0',
                                    position: 'relative',
                                    width: 'fit-content',
                                    minWidth: '280px',
                                    height: '48px'
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: planDuration === 'monthly' ? '4px' : '50%',
                                        top: '4px',
                                        width: '50%',
                                        height: 'calc(100% - 8px)',
                                        background: '#000000',
                                        borderRadius: '20px',
                                        transition: 'left 0.3s ease',
                                        zIndex: 1
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        zIndex: 2,
                                        cursor: 'pointer',
                                        py: 1
                                    }}
                                    onClick={() => setPlanDuration('monthly')}
                                >
                                    <Typography
                                        sx={{
                                            color: planDuration === 'monthly' ? '#FFFFFF' : '#000000',
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            lineHeight: 1
                                        }}
                                    >
                                        Monthly
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#22B060',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            lineHeight: 1,
                                            mt: 0.5
                                        }}
                                    >
                                        30% OFF
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        zIndex: 2,
                                        cursor: 'pointer',
                                        py: 1
                                    }}
                                    onClick={() => setPlanDuration('yearly')}
                                >
                                    <Typography
                                        sx={{
                                            color: planDuration === 'yearly' ? '#FFFFFF' : '#000000',
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            lineHeight: 1
                                        }}
                                    >
                                        Yearly
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#22B060',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            lineHeight: 1,
                                            mt: 0.5
                                        }}
                                    >
                                        50% OFF
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <FormGroup>
                                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                    <Typography color={planDuration === 'monthly' ? "#22B060" : "#000000"} fontWeight="500">
                                        Monthly
                                    </Typography>
                                    <AntSwitch
                                        checked={planDuration === 'yearly'}
                                        onChange={handleDurationChange}
                                        inputProps={{ "aria-label": "plan duration toggle" }}
                                    />
                                    <Typography display="flex" color={planDuration === 'yearly' ? "#22B060" : "#000000"} alignItems="center" gap={1} fontWeight="500">
                                        Yearly{" "}
                                        <Typography component="span" color="#2B2B30" fontSize={{ xs: "12px", sm: "13px" }} fontWeight={500}>
                                            (2 Months Free)
                                        </Typography>
                                    </Typography>
                                </Stack>
                            </FormGroup>
                        )}
                    </Box>
                    {/* ) : null} */}
                </Box>
                {tabValue === 0 ? (
                    <Box className="ew-plan-boxes-con" mt={2}>
                        <Grid container spacing={2} width="100%" justifyContent={{ xs: "center", md: "unset" }}>
                            {/* Launch Plan */}
                            <Grid size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }} maxWidth={{ xs: "350px", md: "unset" }} >
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', minHeight: 420, display: 'flex', flexDirection: 'column', border: '1px solid var(--Stroke-color, #E8EAF0)', background: 'var(--White, #FFF)', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)', height: '100%', position: 'relative' }}>
                                    {/* Live Badge */}
                                    <Box sx={{
                                        background: 'rgb(237, 247, 237)',
                                        color: 'rgb(46, 125, 50)',
                                        border: '1px solid rgb(76, 175, 80)',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        width: 'fit-content',
                                    }}>
                                        Live
                                    </Box>

                                    {/* Currently in trial badge */}
                                    {isCurrentTrialPlan('live') && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            background: '#22B060',
                                            color: '#fff',
                                            p: "4px 8px 4px 12px",
                                            borderRadius: '0 8px 0 8px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            zIndex: 2
                                        }}>
                                            Currently in trial
                                        </Box>
                                    )}

                                    <Typography variant="h6" fontWeight={600} mb={1} align="left" sx={{ mt: "12px" }}>Launch</Typography>
                                    <Typography variant="body2" color="#666" mb={{ sm: "42px", md: "37px" }} align="left" sx={{ fontSize: {xs:'14px', lg: "13px", xl: "14px"} }}>
                                        Host live webinars, start strong.
                                    </Typography>

                                    {/* Attendee Selection */}
                                    <Box width="100%" mb={3} display="flex" flexDirection="column">
                                        <Typography variant="body2" fontWeight={500} color="#666" mb={1} sx={{ fontSize: '14px', alignSelf: 'flex-start' }}>
                                            Attendees
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            width: 'fit-content',
                                            maxWidth: '208px',
                                            background: '#FFF',
                                            borderRadius: '6px',
                                            p: 0.5,
                                            gap: 0.5,
                                            border: '1px solid #E4E4E7'
                                        }}>
                                            {LIVE_PRICES.slice(0, 3).map((option, index) => (
                                                <Button
                                                    key={option.value}
                                                    variant={liveValue === option.value ? "contained" : "text"}
                                                    onClick={() => setLiveValue(option.value)}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        py: "6px",
                                                        px: 2,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        borderRadius: '4px',
                                                        textTransform: 'none',
                                                        background: liveValue === option.value ? '#3F3F3F' : 'transparent',
                                                        color: liveValue === option.value ? '#fff' : '#888888 !important',
                                                        '&:hover': {
                                                            background: liveValue === option.value ? '#3F3F3F' : 'rgba(43, 43, 48, 0.1)',
                                                            boxShadow: 'none !important',
                                                        }
                                                    }}
                                                >
                                                    {option.value}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Typography variant="h5" fontWeight={700} color="#222" display="flex" alignItems="center" mb={1}>
                                        {/* when yearly show monthly price */}
                                        {planDuration === 'yearly' && !saleLive && (
                                            <Typography component="span" fontSize={24} color="rgb(125, 128, 141)" fontWeight={500} ml={1} mr={1} sx={{ textDecoration: 'line-through' }}>{liveSelected.monthly}</Typography>
                                        )}
                                        {saleLive ? (
                                            <>
                                                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                    $<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? liveSelected.yearlySalePrice : liveSelected.monthly}</Typography>
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    $<Typography component="span" fontWeight="inherit" fontSize={34}>
                                                        {planDuration === 'yearly'
                                                            ? Math.round((liveSelected.yearlySalePrice ?? 0) * 0.50)
                                                            : Math.round((liveSelected.monthly ?? 0) * 0.70)
                                                        }
                                                    </Typography>
                                                </span>
                                            </>
                                        ) : (
                                            <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? liveSelected.annual : liveSelected.monthly}</Typography></span>
                                        )}
                                        <Typography component="span" fontSize={16} color="#666666" fontWeight={400} ml={1}>/month</Typography>
                                    </Typography>
                                    
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size='large'
                                        onClick={() => getStartForFree('live')}
                                        sx={{
                                            width: '100%',
                                            mb: 1,
                                            pointerEvents: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? 'none' : 'auto',
                                            bgcolor: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? 'rgba(79, 140, 255, 0.1)' : '#2B2B30',
                                            color: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? '#2B2B30' : '#fff',
                                            border: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? '1px solid #4F8CFF' : undefined,
                                            boxShadow: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? 'none' : undefined,
                                            '&:hover': {
                                                bgcolor: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? 'rgba(79, 140, 255, 0.2)' : '#2B2B30',
                                                boxShadow: (isCurrentPlan('live') && !isCurrentTrialPlan('live')) ? 'none' : undefined,
                                            }
                                        }}
                                    >
                                        {isCurrentTrialPlan('live') ? 'Buy Now' : (isCurrentPlan('live') ? 'Current Plan' : 'Buy this plan')}
                                    </Button>
                                    <Box sx={{ mt: 2 }}>
                                        {[
                                            {
                                                title: "What's Included",
                                                list: [
                                                    { label: '2-hour live sessions', tooltip: '' },
                                                    { label: '1 host + 1 co-host', tooltip: '' },
                                                    { label: 'Up to 4 live webinars', tooltip: '' },
                                                    { label: 'Green room access', tooltip: '' },
                                                    { label: 'Dynamic presentation control', tooltip: '' },
                                                    { label: 'Offers, polls & Q&A', tooltip: '' },
                                                    { label: '2 GB storage', tooltip: '' }
                                                ]
                                            },
                                            {
                                                title: 'External CRM Integrations',
                                                list: [
                                                    { label: 'Essential CRM integrations', tooltip: 'Sync webinar leads and engagement data with essential Launch plan integrations. See plan comparison for details.' }
                                                ]
                                            }
                                        ].map((section, sectionIndex) => (
                                            <Box key={sectionIndex} sx={{ mb: sectionIndex === 0 ? 2 : 0 }}>
                                                <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>
                                                    {section.title}
                                                </Typography>
                                                <Box>
                                                    {section.list.map((feature, featureIndex) => (
                                                        <Typography key={featureIndex} variant="body2" color="#4a4a4a" sx={{ mb: 0.85, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}><span>✓</span> <span>{feature.label}</span></Box>
                                                            {!!feature?.tooltip && (
                                                                <Tooltip arrow title={feature.tooltip || ''}>
                                                                    <InfoIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                            {/* Professional Plan */}
                            <Grid size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }} mt={{ xs: "24px", lg: 0 }} maxWidth={{ xs: "350px", md: "unset" }} >
                                <Paper elevation={0} sx={{
                                    p: 3,
                                    pb: "12px",
                                    borderRadius: '12px',
                                    minHeight: 420,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid var(--Stroke-color, #E8EAF0)',
                                    background: 'var(--White, #FFF)',
                                    boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'visible'
                                }}>
                                    {/* Live + Automated Badge */}
                                    <Box sx={{

                                        background: 'rgb(237, 247, 237)',
                                        color: 'rgb(46, 125, 50)',
                                        border: '1px solid rgb(76, 175, 80)',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        width: 'fit-content',
                                    }}>
                                        Live + Automated
                                    </Box>

                                    {/* Most Popular Badge or Currently in trial badge */}
                                    {isCurrentTrialPlan('liveAuto') ? (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            background: '#22B060',
                                            color: '#fff',
                                            p: "4px 8px 4px 12px",
                                            borderRadius: '0 8px 0 8px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            zIndex: 2
                                        }}>
                                            Currently in trial
                                        </Box>
                                    ) : (
                                        <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: "calc(100% - 12px)",
                                            width: "100%",
                                            right: 0,
                                            background: "#22B060",
                                            color: "#fff",
                                            p: "8px 8px 4px 12px",
                                            borderRadius: "12px 12px 0 0",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            zIndex: 2,
                                        }}
                                        >
                                        Most Popular{" "}
                                        <span style={{ display: "flex", alignItems: "center" }}>
                                            <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="17"
                                            height="16"
                                            viewBox="0 0 17 16"
                                            fill="none"
                                            >
                                            <g clip-path="url(#clip0_26227_189090)">
                                                <path
                                                d="M6.99994 16.0004C6.84193 16.0012 6.68743 15.9538 6.5571 15.8644C6.42676 15.7751 6.3268 15.6481 6.27057 15.5004L5.02557 12.2629C5.00035 12.1977 4.96178 12.1384 4.91232 12.089C4.86286 12.0395 4.80362 12.0009 4.73838 11.9757L1.49994 10.7298C1.35242 10.6732 1.22554 10.5731 1.13604 10.4429C1.04654 10.3127 0.998626 10.1584 0.998626 10.0004C0.998626 9.84239 1.04654 9.6881 1.13604 9.55788C1.22554 9.42766 1.35242 9.32764 1.49994 9.27103L4.73744 8.02603C4.80268 8.00081 4.86193 7.96224 4.91138 7.91278C4.96084 7.86333 4.99941 7.80408 5.02463 7.73884L6.27057 4.5004C6.32718 4.35288 6.4272 4.226 6.55742 4.1365C6.68764 4.047 6.84193 3.99909 6.99994 3.99909C7.15795 3.99909 7.31225 4.047 7.44246 4.1365C7.57268 4.226 7.6727 4.35288 7.72932 4.5004L8.97432 7.7379C8.99953 7.80314 9.0381 7.86239 9.08756 7.91184C9.13702 7.9613 9.19627 7.99987 9.2615 8.02509L12.4803 9.26353C12.6338 9.32043 12.766 9.42327 12.859 9.55803C12.952 9.69279 13.0012 9.85293 12.9999 10.0167C12.9976 10.1719 12.9486 10.3229 12.8595 10.45C12.7703 10.5772 12.6451 10.6746 12.4999 10.7298L9.26244 11.9748C9.1972 12 9.13795 12.0386 9.0885 12.088C9.03904 12.1375 9.00047 12.1967 8.97525 12.262L7.72932 15.5004C7.67308 15.6481 7.57312 15.7751 7.44279 15.8644C7.31245 15.9538 7.15796 16.0012 6.99994 16.0004Z"
                                                fill="white"
                                                />
                                                <path
                                                d="M3.24994 5.5004C3.1573 5.5004 3.06684 5.47233 2.99047 5.41989C2.9141 5.36745 2.85541 5.29311 2.82213 5.20665L2.29525 3.83665C2.28383 3.80669 2.26621 3.77948 2.24354 3.75681C2.22086 3.73413 2.19365 3.71651 2.16369 3.70509L0.793691 3.17821C0.707247 3.14493 0.632915 3.08623 0.580488 3.00986C0.528062 2.93349 0.5 2.84303 0.5 2.7504C0.5 2.65777 0.528062 2.56731 0.580488 2.49094C0.632915 2.41457 0.707247 2.35588 0.793691 2.32259L2.16369 1.79571C2.19362 1.78424 2.22081 1.7666 2.24347 1.74393C2.26614 1.72127 2.28378 1.69408 2.29525 1.66415L2.81744 0.306339C2.84689 0.226403 2.89757 0.156002 2.96402 0.102705C3.03048 0.0494072 3.1102 0.0152275 3.19463 0.00383935C3.29599 -0.00848245 3.39856 0.0133859 3.48608 0.0659766C3.5736 0.118567 3.64105 0.19887 3.67775 0.294152L4.20463 1.66415C4.2161 1.69408 4.23374 1.72127 4.25641 1.74393C4.27908 1.7666 4.30626 1.78424 4.33619 1.79571L5.70619 2.32259C5.79263 2.35588 5.86697 2.41457 5.91939 2.49094C5.97182 2.56731 5.99988 2.65777 5.99988 2.7504C5.99988 2.84303 5.97182 2.93349 5.91939 3.00986C5.86697 3.08623 5.79263 3.14493 5.70619 3.17821L4.33619 3.70509C4.30623 3.71651 4.27902 3.73413 4.25635 3.75681C4.23367 3.77948 4.21605 3.80669 4.20463 3.83665L3.67775 5.20665C3.64447 5.29311 3.58578 5.36745 3.50941 5.41989C3.43304 5.47233 3.34258 5.5004 3.24994 5.5004Z"
                                                fill="white"
                                                />
                                                <path
                                                d="M12.9999 8.0004C12.8989 8.00037 12.8002 7.96972 12.7169 7.91248C12.6336 7.85524 12.5696 7.77411 12.5334 7.67978L11.8196 5.82446C11.8071 5.79177 11.7878 5.76208 11.763 5.73732C11.7383 5.71255 11.7086 5.69327 11.6759 5.68071L9.82057 4.96696C9.72631 4.93064 9.64526 4.86663 9.5881 4.78334C9.53094 4.70006 9.50034 4.60142 9.50034 4.5004C9.50034 4.39939 9.53094 4.30074 9.5881 4.21746C9.64526 4.13418 9.72631 4.07016 9.82057 4.03384L11.6759 3.32009C11.7086 3.30753 11.7383 3.28825 11.763 3.26349C11.7878 3.23872 11.8071 3.20903 11.8196 3.17634L12.5281 1.33415C12.5604 1.24704 12.6158 1.17032 12.6882 1.11215C12.7607 1.05399 12.8476 1.01656 12.9396 1.00384C13.0502 0.99045 13.1621 1.01439 13.2576 1.07185C13.3531 1.12931 13.4266 1.21701 13.4665 1.32103L14.1803 3.17634C14.1928 3.20903 14.2121 3.23872 14.2369 3.26349C14.2616 3.28825 14.2913 3.30753 14.324 3.32009L16.1793 4.03384C16.2736 4.07016 16.3546 4.13418 16.4118 4.21746C16.4689 4.30074 16.4995 4.39939 16.4995 4.5004C16.4995 4.60142 16.4689 4.70006 16.4118 4.78334C16.3546 4.86663 16.2736 4.93064 16.1793 4.96696L14.324 5.68071C14.2913 5.69327 14.2616 5.71255 14.2369 5.73732C14.2121 5.76208 14.1928 5.79177 14.1803 5.82446L13.4665 7.67978C13.4302 7.77411 13.3663 7.85524 13.283 7.91248C13.1997 7.96972 13.101 8.00037 12.9999 8.0004Z"
                                                fill="white"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_26227_189090">
                                                <rect
                                                    width="16"
                                                    height="16"
                                                    fill="white"
                                                    transform="translate(0.5)"
                                                />
                                                </clipPath>
                                            </defs>
                                            </svg>
                                        </span>
                                        </Box>
                                    )}

                                    <Typography variant="h6" fontWeight={600} mb={1} align="left" sx={{ mt: "12px" }}>Professional
                                        <Box component="span" sx={{ fontSize: 12, color: '#999', ml: 1, fontWeight: 400 }}>Now With
                                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}> AI</Box>
                                        </Box>
                                    </Typography>
                                    
                                    <Typography variant="body2" color="#666" mb={2} align="left" sx={{ fontSize: {xs:'14px', lg: "13px", xl: "14px"} }}>
                                        All-in-one solution for live and automated webinars
                                    </Typography>

                                    {/* Attendee Selection */}
                                    <Box width="100%" mb={3} display="flex" flexDirection="column" >
                                        <Typography variant="body2" fontWeight={500} color="#666" mb={1} sx={{ fontSize: '14px', alignSelf: 'flex-start' }}>
                                            Attendees
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            width: '100%',
                                            maxWidth: '208px',
                                            background: '#FFF',
                                            borderRadius: '6px',
                                            p: 0.5,
                                            gap: 0.5,
                                            border: '1px solid #E4E4E7'
                                        }}>
                                            {LIVE_AUTO_PRICES.slice(0, 3).map((option, index) => (
                                                <Button
                                                    key={option.value}
                                                    variant={liveAutomatedValue === option.value ? "contained" : "text"}
                                                    onClick={() => setLiveAutomatedValue(option.value)}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        py: "6px",
                                                        px: 1,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        borderRadius: '4px',
                                                        textTransform: 'none',
                                                        background: liveAutomatedValue === option.value ? '#3F3F3F' : 'transparent',
                                                        color: liveAutomatedValue === option.value ? '#fff' : '#888888 !important',
                                                        '&:hover': {
                                                            background: liveAutomatedValue === option.value ? '#3F3F3F' : 'rgba(43, 43, 48, 0.1)',
                                                            boxShadow: 'none !important',
                                                        }
                                                    }}
                                                >
                                                    {option.value}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Typography variant="h5" fontWeight={700} color="#222" display="flex" alignItems="center" mb={1}>
                                        {liveAutoSelected.custom ? (
                                            <Typography variant="h5" fontWeight={700} fontSize="16px" mb={0} my="5px" color={theme.palette.primary.main} textAlign="center">
                                                Get a quote
                                            </Typography>
                                        ) : (
                                            <>
                                                {/* when yearly show monthly price */}
                                                {planDuration === 'yearly' && !saleLive && (
                                                    <Typography component="span" fontSize={24} color="rgb(125, 128, 141)" fontWeight={500} ml={1} mr={1} sx={{ textDecoration: 'line-through' }}>{liveAutoSelected.monthly}</Typography>
                                                )}
                                                {saleLive ? (
                                                    <>
                                                        <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                            $<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? liveAutoSelected.yearlySalePrice : liveAutoSelected.monthly}</Typography>
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                                            $<Typography component="span" fontWeight="inherit" fontSize={34}>
                                                                {planDuration === 'yearly'
                                                                    ? Math.round((liveAutoSelected.yearlySalePrice ?? 0) * 0.50)
                                                                    : Math.round((liveAutoSelected.monthly ?? 0) * 0.70)
                                                                }
                                                            </Typography>
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? liveAutoSelected.annual : liveAutoSelected.monthly}</Typography></span>
                                                )}
                                                <Typography component="span" fontSize={16} color="#666666" fontWeight={400} ml={1}>/month</Typography>
                                            </>
                                        )}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size='large'
                                        onClick={() => liveAutoSelected.custom ? contactSales() : getStartForFree('liveAuto')}
                                        sx={{
                                            width: '100%',
                                            mb: 1,
                                            pointerEvents: liveAutoSelected.custom ? 'auto' : ((isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? 'none' : 'auto'),
                                            bgcolor: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? 'rgba(79, 140, 255, 0.1)' : '#4f8cff',
                                            color: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? '#4f8cff' : '#fff',
                                            border: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? '1px solid #4F8CFF' : undefined,
                                            boxShadow: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? 'none' : undefined,
                                            '&:hover': {
                                                bgcolor: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? 'rgba(79, 140, 255, 0.2)' : '#3B82F6',
                                                boxShadow: !liveAutoSelected.custom && (isCurrentPlan('liveAuto') && !isCurrentTrialPlan('liveAuto')) ? 'none' : undefined,
                                            }
                                        }}
                                    >
                                        {isCurrentTrialPlan('liveAuto') ? 'Buy Now' : (!liveAutoSelected.custom && isCurrentPlan('liveAuto') ? 'Current Plan' : (liveAutoSelected.custom ? "Contact Sales" : "Buy this plan"))}
                                    </Button>
                                    {/* <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: '#18181B9E',
                                            mt: 1,
                                            fontSize: '12px',
                                            fontWeight: 400
                                        }}
                                    >
                                        *Includes RTMP & EasyCast (Direct YouTube Streaming)
                                    </Typography> */}
                                    <Box sx={{ mt: 2 }}>
                                        {[
                                            {
                                                title: "What's Included",
                                                list: [
                                                { label: "AI Webinar Builder", tooltip: "" },
                                                { label: "4-hour live sessions", tooltip: "" },
                                                { label: "Up to 5 active webinars", tooltip: "" },
                                                { label: "Simulive, On-Demand & Evergreen", tooltip: "" },
                                                { label: "Stream to YouTube or via RTMP", tooltip: "" },
                                                { label: "Simulated + live chat support", tooltip: "" },
                                                { label: "Up to 3 co-hosts", tooltip: "" },
                                                { label: "Advanced analytics & reporting", tooltip: "" },
                                                { label: "4 GB storage", tooltip: "" },
                                                { label: "Priority support & manager", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "Inbuilt Sales CRM",
                                                list: [
                                                { label: "Auto-capture webinar leads", tooltip: "" },
                                                { label: "Import & manage lead lists", tooltip: "" },
                                                { label: "Engagement-based lead scoring", tooltip: "" },
                                                { label: "Hot / Warm / Nurture segmentation", tooltip: "" },
                                                { label: "Automated email & call follow-ups", tooltip: "" },
                                                { label: "Built-in auto dialer", tooltip: "" },
                                                { label: "Sales pipeline tracking", tooltip: "" },
                                                { label: "Customer activity timeline", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "External CRM Integrations",
                                                list: [
                                                { label: "Access to all CRM integrations", tooltip: "Sync webinar leads and engagement data directly to your CRM with access to all available integrations." }
                                                ]
                                            }
                                            ].map((section, sectionIndex) => (
                                            <Box key={sectionIndex} sx={{ mb: sectionIndex === 2 ? 0 : 2 }}>
                                                <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>
                                                    {section.title}
                                                </Typography>
                                                <Box>
                                                    {section.list.map((feature, featureIndex) => (
                                                        <Typography key={featureIndex} variant="body2" color="#4a4a4a" sx={{ mb: 0.85, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}><span>✓</span> <span>{feature.label}</span></Box>
                                                            {!!feature?.tooltip && (
                                                                <Tooltip arrow title={feature.tooltip || ''}>
                                                                    <InfoIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Scale Plan */}
                            <Grid size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }} maxWidth={{ xs: "350px", md: "unset" }} >
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', minHeight: 420, display: 'flex', flexDirection: 'column', border: '1px solid var(--Stroke-color, #E8EAF0)', background: 'var(--White, #FFF)', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)', height: '100%', position: 'relative' }}>
                                <Box
                                    sx={{
                                    position: "absolute",
                                    bottom: "calc(100% - 12px)",
                                    width: "100%",
                                    right: 0,
                                    background: "#D5E1D7",
                                    color: "#272323",
                                    p: "8px 8px 4px 12px",
                                    borderRadius: "12px 12px 0 0",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    textAlign: "center",
                                    zIndex: 2,
                                    }}
                                >
                                    Great for evergreen funnels
                                </Box>
                                    {/* Automated Badge */}
                                    <Box sx={{

                                        background: 'rgb(237, 247, 237)',
                                        color: 'rgb(46, 125, 50)',
                                        border: '1px solid rgb(76, 175, 80)',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        width: 'fit-content',
                                    }}>
                                        Automated
                                    </Box>

                                    {/* Currently in trial badge */}
                                    {isCurrentTrialPlan('automated') && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            background: '#22B060',
                                            color: '#fff',
                                            p: "4px 8px 4px 12px",
                                            borderRadius: '0 8px 0 8px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            zIndex: 2
                                        }}>
                                            Currently in trial
                                        </Box>
                                    )}

                                    <Typography variant="h6" fontWeight={600} mb={1} align="left" sx={{ mt: "12px" }}>Scale
                                    <Box component="span" sx={{ fontSize: 12, color: '#999', ml: 1, fontWeight: 400 }}>
                                    </Box>
                                    </Typography>
                                    <Typography variant="body2" color="#666" mb={2} align="left" sx={{ fontSize: {xs:'14px', lg: "13px", xl: "14px"} }}>
                                        If you want only automated sessions, with higher attendee limits for lead generation & 24/7 sales.
                                    </Typography>

                                    {/* Attendee Selection */}
                                    <Box width="100%" mb={3} display="flex" flexDirection="column">
                                        <Typography variant="body2" fontWeight={500} color="#666" mb={1} sx={{ fontSize: '14px', alignSelf: 'flex-start' }}>
                                            Attendees
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            width: '100%',
                                            maxWidth: '208px',
                                            background: '#FFF',
                                            borderRadius: '6px',
                                            p: 0.5,
                                            gap: 0.5,
                                            border: '1px solid #E4E4E7'
                                        }}>
                                            {AUTOMATED_PRICES.slice(0, 3).map((option, index) => (
                                                <Button
                                                    key={option.value}
                                                    variant={automatedValue === option.value ? "contained" : "text"}
                                                    onClick={() => setAutomatedValue(option.value)}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        py: "6px",
                                                        px: 1,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        borderRadius: '4px',
                                                        textTransform: 'none',
                                                        background: automatedValue === option.value ? '#3F3F3F' : 'transparent',
                                                        color: automatedValue === option.value ? '#fff' : '#888888 !important',
                                                        '&:hover': {
                                                            background: automatedValue === option.value ? '#3F3F3F' : 'rgba(43, 43, 48, 0.1)',
                                                            boxShadow: 'none !important',
                                                        }
                                                    }}
                                                >
                                                    {option.value}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Typography variant="h5" fontWeight={700} color="#222" display="flex" alignItems="center" mb={1}>
                                        {/* when yearly show monthly price */}
                                        {planDuration === 'yearly' && !saleLive && (
                                            <Typography component="span" fontSize={24} color="rgb(125, 128, 141)" fontWeight={500} ml={1} mr={1} sx={{ textDecoration: 'line-through' }}>{automatedSelected.monthly}</Typography>
                                        )}
                                        {saleLive ? (
                                            <>
                                                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                    $<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? automatedSelected.yearlySalePrice : automatedSelected.monthly}</Typography>
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    $<Typography component="span" fontWeight="inherit" fontSize={34}>
                                                        {planDuration === 'yearly'
                                                            ? Math.round((automatedSelected.yearlySalePrice ?? 0) * 0.50)
                                                            : Math.round((automatedSelected.monthly ?? 0) * 0.70)
                                                        }
                                                    </Typography>
                                                </span>
                                            </>
                                        ) : (
                                            <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={34}>{planDuration === 'yearly' ? automatedSelected.annual : automatedSelected.monthly}</Typography></span>
                                        )}
                                        <Typography component="span" fontSize={16} color="#666666" fontWeight={400} ml={1}>/month</Typography>
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size='large'
                                        onClick={() => getStartForFree('automated')}
                                        sx={{
                                            width: '100%',
                                            mb: 1,
                                            pointerEvents: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? 'none' : 'auto',
                                            bgcolor: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? 'rgba(79, 140, 255, 0.1)' : '#2B2B30',
                                            color: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? '#2B2B30' : '#fff',
                                            border: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? '1px solid #4F8CFF' : undefined,
                                            boxShadow: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? 'none' : undefined,
                                            '&:hover': {
                                                bgcolor: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? 'rgba(79, 140, 255, 0.2)' : '#2B2B30',
                                                boxShadow: (isCurrentPlan('automated') && !isCurrentTrialPlan('automated')) ? 'none' : undefined,
                                            }
                                        }}
                                    >
                                        {isCurrentTrialPlan('automated') ? 'Buy Now' : (isCurrentPlan('automated') ? 'Current Plan' : 'Buy this plan')}
                                    </Button>
                                    <Box sx={{ mt: 2 }}>
                                        {[
                                            {
                                                title: "What's Included",
                                                list: [
                                                { label: "AI Webinar Builder", tooltip: "" },
                                                { label: "Up to 5 active webinars", tooltip: "" },
                                                { label: "Simulive, On-Demand & Evergreen", tooltip: "" },
                                                { label: "Simulated + live chat options", tooltip: "" },
                                                { label: "Evergreen webinars with CTAs & polls", tooltip: "" },
                                                { label: "Funnel optimization features", tooltip: "" },
                                                { label: "Advanced analytics & reporting", tooltip: "" },
                                                { label: "Priority support & manager", tooltip: "" },
                                                { label: "4 GB storage", tooltip: "" },
                                                { label: "Built for always-on conversions", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "Inbuilt Sales CRM (Replaces Your CRM)",
                                                list: [
                                                { label: "Auto-capture webinar leads", tooltip: "" },
                                                { label: "Import & manage lead lists", tooltip: "" },
                                                { label: "Engagement-based lead scoring", tooltip: "" },
                                                { label: "Hot / Warm / Nurture segmentation", tooltip: "" },
                                                { label: "Automated email & call follow-ups", tooltip: "" },
                                                { label: "Built-in auto dialer", tooltip: "" },
                                                { label: "Sales pipeline tracking", tooltip: "" },
                                                { label: "Customer activity timeline", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "External CRM Integrations",
                                                list: [
                                                { label: "Access to all CRM integrations", tooltip: "Sync webinar leads and engagement data directly to your CRM with access to all available integrations." }
                                                ]
                                            }
                                            ].map((section, sectionIndex) => (
                                            <Box key={sectionIndex} sx={{ mb: sectionIndex === 2 ? 0 : 2 }}>
                                                <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>
                                                    {section.title}
                                                </Typography>
                                                <Box>
                                                    {section.list.map((feature, featureIndex) => (
                                                        <Typography key={featureIndex} variant="body2" color="#4a4a4a" sx={{ mb: 0.85, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}><span>✓</span> <span>{feature.label}</span></Box>
                                                            {!!feature?.tooltip && (
                                                                <Tooltip arrow title={feature.tooltip || ''}>
                                                                    <InfoIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>


                            {/* Enterprise Plan */}
                            <Grid size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }} maxWidth={{ xs: "350px", md: "unset" }} >
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', minHeight: 420, display: 'flex', flexDirection: 'column', border: '1px solid var(--Stroke-color, #E8EAF0)', background: 'var(--White, #FFF)', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)', height: '100%', position: 'relative' }}>
                                    <Box sx={{height: 35}} />
                                    <Typography variant="h6" fontWeight={600} mb={1} align="left">Enterprise
                                        <Box component="span" sx={{ fontSize: 12, color: '#999', ml: 1, fontWeight: 400 }}>Now With
                                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}> AI</Box>
                                        </Box>
                                    </Typography>
                                    <Typography variant="body2" color="#666" mb={2} align="left" sx={{ fontSize: '14px' }}>
                                        Custom features, dedicated success manager, built to scale results.
                                    </Typography>

                                    {/* Attendee Selection */}
                                    <Box width="100%" mb={3} display="flex" flexDirection="column" >
                                        <Typography variant="body2" color="#666" mb={1} sx={{ fontSize: '14px', alignSelf: 'flex-start' }}>
                                            Attendees
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            background: '#FFF',
                                            width: 'fit-content',
                                            borderRadius: '6px',
                                            p: 0.5,
                                            gap: 0.5,
                                            border: '1px solid #E4E4E7'
                                        }}>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    py: 1,
                                                    px: 2,
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    borderRadius: '4px',
                                                    textTransform: 'none',
                                                    background: '#3F3F3F',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        background: '#3F3F3F',
                                                    }
                                                }}
                                            >
                                                Upto 50,000
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Typography variant="h5" fontWeight={700} color="#222" display="flex" alignItems="center" justifyContent="center" mb={2} fontSize="32px">
                                        Get a quote
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        size='large'
                                        onClick={() => contactSales()}
                                        sx={{
                                            width: '100%',
                                            mb: 1,
                                            border: '1px solid #2B2B30',
                                            color: '#2B2B30',
                                            background: '#fff',
                                            '&:hover': {
                                                background: '#2B2B30',
                                                color: '#fff',
                                                border: '1px solid #2B2B30',
                                            }
                                        }}
                                    >
                                        Book a demo
                                    </Button>
                                    <Box sx={{ mt: 2 }}>
                                        {[
                                            {
                                                title: "What's Included",
                                                list: [
                                                { label: "AI Webinar Builder", tooltip: "" },
                                                { label: "4-hour live sessions", tooltip: "" },
                                                { label: "Unlimited Sessions (Live & Automated)", tooltip: "" },
                                                { label: "Simulive, On-Demand & Evergreen", tooltip: "" },
                                                { label: "Stream directly to YouTube or via RTMP", tooltip: "" },
                                                { label: "API access for integrations", tooltip: "" },
                                                { label: "White-label & custom domains", tooltip: "" },
                                                { label: "Dedicated account manager", tooltip: "" },
                                                { label: "VIP support + live monitoring", tooltip: "" },
                                                { label: "Unlimited storage & members", tooltip: "" },
                                                { label: "Early access to beta features", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "Inbuilt Sales CRM (Replaces Your CRM)",
                                                list: [
                                                { label: "Auto-capture webinar leads", tooltip: "" },
                                                { label: "Import & manage lead lists", tooltip: "" },
                                                { label: "Engagement-based lead scoring", tooltip: "" },
                                                { label: "Hot / Warm / Nurture segmentation", tooltip: "" },
                                                { label: "Automated email & call follow-ups", tooltip: "" },
                                                { label: "Built-in auto dialer", tooltip: "" },
                                                { label: "Sales pipeline tracking", tooltip: "" },
                                                { label: "Customer activity timeline", tooltip: "" }
                                                ]
                                            },
                                            {
                                                title: "External CRM Integrations",
                                                list: [
                                                { label: "Access to all CRM integrations", tooltip: "Sync webinar leads and engagement data with essential Launch plan integrations. See plan comparison for details." }
                                                ]
                                            }
                                            ].map((section, sectionIndex) => (
                                            <Box key={sectionIndex} sx={{ mb: sectionIndex === 2 ? 0 : 2 }}>
                                                <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>
                                                    {section.title}
                                                </Typography>
                                                <Box>
                                                    {section.list.map((feature, featureIndex) => (
                                                        <Typography key={featureIndex} variant="body2" color="#4a4a4a" sx={{ mb: 0.85, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}><span>✓</span> <span>{feature.label}</span></Box>
                                                            {!!feature?.tooltip && (
                                                                <Tooltip arrow title={feature.tooltip || ''}>
                                                                    <InfoIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', background: '#fff', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)' }} overflow="visible" position="relative">
                        <Box position="sticky" top={0} zIndex={1} display="flex" width="100%" minWidth={1100} border="1px solid #E8EAF0" borderRadius="12px" bgcolor="#ffffff">
                            {/* Info column */}
                            <Box sx={{ minWidth: 240, background: 'linear-gradient(135deg, #4F8CFF 0%, #6DC7FF 100%)', color: '#fff', borderTopLeftRadius: 12, borderBottomLeftRadius: 12, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h5" fontWeight={700} mb={1}>Compare plans</Typography>
                            </Box>
                            {/* Plan columns */}
                            {[{
                                name: 'Launch',
                                color: theme.palette.primary.main,
                                price: saleLive
                                    ? (planDuration === 'yearly'
                                        ? Math.round((liveSelected.yearlySalePrice ?? 0) * 0.50)
                                        : Math.round((liveSelected.monthly ?? 0) * 0.70))
                                    : (planDuration === 'yearly' ? liveSelected.annual : liveSelected.monthly),
                                priceLabel: planDuration === 'yearly' ? '/month' : '/month',
                                dropdown: (
                                    <Select
                                        sx={{ width: '90%', mb: 1, mt: 1, background: '#fff' }}
                                        size="small"
                                        value={liveValue}
                                        onChange={e => setLiveValue(e.target.value)}
                                        MenuProps={{ PaperProps: { sx: { '& ul': { p: "4px" }, '& li': { p: "8px 16px", borderRadius: "4px" } } } }}
                                    >
                                        {LIVE_PRICES.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                ),
                                button: <Button
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{
                                        mt: 1,
                                        pointerEvents: isCurrentPlan('live') ? 'none' : 'auto',
                                        bgcolor: isCurrentPlan('live') ? 'rgba(79, 140, 255, 0.1)' : '#2B2B30',
                                        color: isCurrentPlan('live') ? '#2B2B30' : '#fff',
                                        border: isCurrentPlan('live') ? '1px solid #4F8CFF' : undefined,
                                        boxShadow: isCurrentPlan('live') ? 'none' : undefined,
                                        '&:hover': {
                                            bgcolor: isCurrentPlan('live') ? 'rgba(79, 140, 255, 0.2)' : '#2B2B30',
                                            boxShadow: isCurrentPlan('live') ? 'none' : undefined,
                                        }
                                    }}
                                    onClick={() => getStartForFree('live')}
                                >
                                    {isCurrentPlan('live') ? 'Current Plan' : 'Upgrade'}
                                </Button>,
                            }, {
                                name: 'Scale',
                                color: theme.palette.primary.main,
                                price: saleLive
                                    ? (planDuration === 'yearly'
                                        ? Math.round((automatedSelected.yearlySalePrice ?? 0) * 0.50)
                                        : Math.round((automatedSelected.monthly ?? 0) * 0.70))
                                    : (planDuration === 'yearly' ? automatedSelected.annual : automatedSelected.monthly),
                                priceLabel: planDuration === 'yearly' ? '/month' : '/month',
                                dropdown: (
                                    <Select
                                        sx={{ width: '90%', mb: 1, mt: 1, background: '#fff' }}
                                        size="small"
                                        value={automatedValue}
                                        onChange={e => setAutomatedValue(e.target.value)}
                                        MenuProps={{ PaperProps: { sx: { '& ul': { p: "4px" }, '& li': { p: "8px 16px", borderRadius: "4px" } } } }}
                                    >
                                        {AUTOMATED_PRICES.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                ),
                                button: <Button
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{
                                        mt: 1,
                                        pointerEvents: isCurrentPlan('automated') ? 'none' : 'auto',
                                        bgcolor: isCurrentPlan('automated') ? 'rgba(79, 140, 255, 0.1)' : '#2B2B30',
                                        color: isCurrentPlan('automated') ? '#2B2B30' : '#fff',
                                        border: isCurrentPlan('automated') ? '1px solid #4F8CFF' : undefined,
                                        boxShadow: isCurrentPlan('automated') ? 'none' : undefined,
                                        '&:hover': {
                                            bgcolor: isCurrentPlan('automated') ? 'rgba(79, 140, 255, 0.2)' : '#2B2B30',
                                            boxShadow: isCurrentPlan('automated') ? 'none' : undefined,
                                        }
                                    }}
                                    onClick={() => getStartForFree('automated')}
                                >
                                    {isCurrentPlan('automated') ? 'Current Plan' : 'Upgrade'}
                                </Button>,
                            }, {
                                name: 'Professional',
                                color: theme.palette.primary.main,
                                price: liveAutoSelected.custom ? "custom" : (saleLive
                                    ? (planDuration === 'yearly'
                                        ? Math.round((liveAutoSelected.yearlySalePrice ?? 0) * 0.50)
                                        : Math.round((liveAutoSelected.monthly ?? 0) * 0.70))
                                    : (planDuration === 'yearly' ? liveAutoSelected.annual : liveAutoSelected.monthly)),
                                priceLabel: planDuration === 'yearly' ? '/month' : '/month',
                                dropdown: (
                                    <Select
                                        sx={{ width: '90%', mb: 1, mt: 1, background: '#fff' }}
                                        size="small"
                                        value={liveAutomatedValue}
                                        onChange={e => setLiveAutomatedValue(e.target.value)}
                                        MenuProps={{ PaperProps: { sx: { '& ul': { p: "4px" }, '& li': { p: "8px 16px", borderRadius: "4px" } } } }}
                                    >
                                        {LIVE_AUTO_PRICES.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                ),
                                button: <Button
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{
                                        mt: 1,
                                        pointerEvents: liveAutoSelected.custom ? 'auto' : (isCurrentPlan('liveAuto') ? 'none' : 'auto'),
                                        bgcolor: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? 'rgba(79, 140, 255, 0.1)' : '#2B2B30',
                                        color: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? '#2B2B30' : '#fff',
                                        border: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? '1px solid #4F8CFF' : undefined,
                                        boxShadow: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? 'none' : undefined,
                                        '&:hover': {
                                            bgcolor: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? 'rgba(79, 140, 255, 0.2)' : '#2B2B30',
                                            boxShadow: !liveAutoSelected.custom && isCurrentPlan('liveAuto') ? 'none' : undefined,
                                        }
                                    }}
                                    onClick={() => liveAutoSelected.custom ? contactSales() : getStartForFree('liveAuto')}
                                >
                                    {liveAutoSelected.custom
                                        ? "Book a call"
                                        : (isCurrentPlan('liveAuto') ? 'Current Plan' : 'Upgrade')}
                                </Button>,
                            }, {
                                name: 'Enterprise',
                                color: theme.palette.primary.main,
                                price: null,
                                priceLabel: '',
                                dropdown: <Typography fontWeight={500} mt="13px" mb={1} fontSize={14} textAlign="center">Up to <br /><span style={{ fontSize: 24, color: theme.palette.primary.main, fontWeight: 600 }}>500K</span><br />attendees</Typography>,
                                button: <Button
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{ mt: 1, bgcolor: '#2B2B30', color: '#fff', border: '1px solid #2B2B30', '&:hover': { bgcolor: '#2B2B30', color: '#fff', border: '1px solid #2B2B30' } }}
                                    onClick={() => contactSales()}
                                >
                                    Upgrade
                                </Button>,
                            }].map((plan, idx) => (
                                <Box key={plan.name} position="relative" sx={{ flex: 1, minWidth: 220, borderRight: idx < 3 ? '1px solid #E8EAF0' : 'none', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
                                    {plan.name === "Live + Automated" && <Box sx={{
                                        position: 'absolute',
                                        top: -24,
                                        left: 0,
                                        width: '100%',
                                        height: 24,
                                        background: theme.palette.primary.main,
                                        borderTopLeftRadius: '12px',
                                        borderTopRightRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 2
                                    }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: 14 }}>
                                            Recommended
                                        </Typography>
                                    </Box>}
                                    <Typography variant="h6" fontWeight={600} color={plan.color} mb={1}>{plan.name}</Typography>
                                    {/* {plan.price === "custom" && <Typography variant="h5" fontWeight={700} fontSize="16px" mb='20px' color={theme.palette.primary.main} textAlign="center">
                                        Contact sales for a quote
                                    </Typography>}
                                    {plan.price !== null && plan.price !== "custom" ? (
                                        <Typography variant="h5" fontWeight={700} color="#222" m={0} mx={0} display="flex" alignItems="center">
                                            {saleLive ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.name === 'Live Webinars'
                                                                ? (planDuration === 'yearly' ? liveSelected.yearlySalePrice : liveSelected.monthly)
                                                                : plan.name === 'Automated'
                                                                    ? (planDuration === 'yearly' ? automatedSelected.yearlySalePrice : automatedSelected.monthly)
                                                                    : (planDuration === 'yearly' ? liveAutoSelected.yearlySalePrice : liveAutoSelected.monthly)
                                                            }
                                                        </Typography>
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.price}
                                                        </Typography>
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={28}>{plan.price}</Typography></span>
                                            )}
                                            <Typography component="span" fontSize={14} color="#666666" fontWeight={400} ml={1}>{plan.priceLabel}</Typography>
                                        </Typography>
                                    ) : ""} */}
                                    {/* 
                                    {plan.dropdown && plan.price !== null && plan.dropdown}

                                    {plan.price === null && plan.dropdown} */}
                                    {plan.button}
                                </Box>
                            ))}
                        </Box>
                        {/* Features Table */}
                        <Box sx={{ width: '100%', minWidth: 1100, background: '#fff', borderRadius: '0 0 12px 12px', boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)', mt: 0 }}>
                            {(showAllFeatures ? FEATURES : FEATURES.slice(0, 9)).map((feature, i) =>
                                feature.section ? (
                                    <Box
                                        key={feature.section}
                                        sx={{
                                            width: '100%',
                                            px: 4,
                                            pt: "14px",
                                            pb: "10px",
                                            color: theme.palette.primary.main,
                                            fontWeight: 500,
                                            fontSize: 22,
                                            background: 'transparent',
                                            letterSpacing: 0.2,
                                            borderTop: "1px solid #E8EAF0",

                                            "& + div": {
                                                borderTop: "none",
                                            }
                                        }}
                                    >
                                        {feature.section}
                                    </Box>
                                ) : (
                                    <Box key={feature.label} display="flex" alignItems="center" sx={{ borderTop: i === 0 ? 'none' : '1px solid #E8EAF0', py: "10px", px: 0 }}>
                                        <Box sx={{ minWidth: 240, pl: 4, color: '#2B2B30', display: 'flex', alignItems: 'center', justifyContent: "space-between", gap: 1, maxWidth: '20%', width: '100%' }}>
                                            {feature.label}
                                            {/* {feature.info && (
                                                <Tooltip arrow title={feature.info}>
                                                    <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                                                        <InfoIcon />
                                                    </Box>
                                                </Tooltip>
                                            )} */}
                                        </Box>
                                        {Array.isArray(feature.plans) && feature.plans.map((val, idx) => (
                                            <Box key={idx} sx={{ flex: 1, minWidth: 220, textAlign: 'center', color: typeof val === 'string' ? '#333' : undefined, fontWeight: typeof val === 'string' ? 500 : 400 }}>
                                                {val === true && <CheckIcon sx={{ color: '#333333', fontSize: 19 }} />}
                                                {val === false && <Typography fontWeight={700}>-</Typography>}
                                                {typeof val === 'string' && val}
                                            </Box>
                                        ))}
                                    </Box>
                                )
                            )}
                            <Box
                                sx={{
                                    background: '#F0F0F0',
                                    px: 3,
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    borderBottomLeftRadius: 12,
                                    borderBottomRightRadius: 12,
                                    border: '1px solid  #E8EAF0',
                                    fontWeight: 500,
                                    fontSize: 'min(18px, 14px + 4 * (100vw - 320px) / 1600)',
                                    color: '#333333',
                                    userSelect: 'none',
                                    mt: 0,
                                    "&:hover": {
                                        background: '#E8EAF0',
                                    }
                                }}
                                onClick={() => setShowAllFeatures((prev) => !prev)}
                            >
                                {showAllFeatures ? 'Show less' : 'Compare plans'}
                                <Box component="span" display="flex" sx={{ ml: 1, transition: 'transform 0.2s', transform: showAllFeatures ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8" fill="none">
                                        <path d="M0.922687 1.50373L5.49992 6.49707L10.0771 1.50373" stroke="#333333" stroke-width="1.66445" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default PricingSectionV2; 